import { SearchInput } from 'components/SearchInput';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { DividerLine } from 'styleguide/DividerLine';
import { Icon } from 'styleguide/Icons';
import { Modal } from 'styleguide/Modal';
import { P1, P2 } from 'styleguide/Texts';
import { View } from 'styleguide/View';
import { themeState } from 'styleguide/theme';
import { ProfileImage } from 'components/ProfileImage';
import { PrimaryButton } from 'styleguide/Buttons';
import { SecondaryButton } from 'styleguide/Buttons';
import { stores } from 'state/stores';
import { ClipLoader } from 'react-spinners';
import { colors } from 'styleguide/colors';
import { UserListModalViewModalInterface } from './types';
import { Link } from 'components/Link';
import { CornerRadius, Spacing } from 'styleguide/spacing';
import { FlexRow } from 'styleguide/Row';
import { t } from 'translations/t';

const UpperContainer = styled(View)`
  align-items: center;
  padding: ${Spacing.lg}px ${Spacing.lg}px ${Spacing.md}px ${Spacing.lg}px;
  gap: ${Spacing.md}px;
`;

const LowerContainer = styled(View)`
  padding: ${Spacing.md}px ${Spacing.lg}px;
  overflow-y: auto;
  gap: ${Spacing.md}px;
`;

const Container = styled(View)`
  width: 500px;
  height: 80vh;
  max-width: 500px;
  @media (max-width: 1024px) {
    width: 80vw;
  }
  border-radius: ${CornerRadius.md}px;
`;

const UserHeader = styled(FlexRow)`
  cursor: pointer;
  gap: ${Spacing.md}px;
`;

export interface UserListModalProps {
  vm: UserListModalViewModalInterface;
  isOpen: boolean;
  onClose: () => void;
}

export const UserListModal = observer(({ vm, isOpen, onClose }: UserListModalProps) => {
  return (
    <ThemeProvider theme={themeState.current}>
      <Modal isOpen={isOpen} onClose={onClose} contentStyle={{ padding: 0 }}>
        <Container style={{ colorScheme: themeState.current.name }}>
          <UpperContainer>
            <FlexRow style={{ justifyContent: 'space-between', width: '100%' }}>
              <P1 style={{ fontWeight: 500 }}>{vm.title}</P1>
              <View style={{ cursor: 'pointer' }} onClick={onClose}>
                <Icon type="cross" tint={colors.grey} />
              </View>
            </FlexRow>
            <SearchInput
              style={{ width: '100%' }}
              value={vm.searchText}
              type={'primary'}
              placeholder={t('web.userListModal.searchPlaceholder')}
              onChange={vm.onSearchTextChange}
            />
          </UpperContainer>
          <DividerLine></DividerLine>
          <LowerContainer onScroll={vm.handleScroll}>
            {vm.users.length ? (
              <View style={{ gap: Spacing.md }}>
                {vm.users.map((user, idx) => {
                  return (
                    <FlexRow key={idx} style={{ justifyContent: 'space-between' }}>
                      <Link href={`/user/${user.username}`}>
                        <UserHeader onClick={onClose}>
                          <ProfileImage source={user.profile_pic} diameter={48} />
                          <View>
                            <P2>{user.username}</P2>
                            <P2 type="secondary">{user.full_name}</P2>
                          </View>
                        </UserHeader>
                      </Link>
                      {user.username !== stores.account.username &&
                        (user.following_status === 'following' ? (
                          <SecondaryButton
                            title={t('followButton.unfollow')}
                            onClick={() => vm.onUnfollowUserClick(user)}
                          />
                        ) : user.following_status === 'not-following' ? (
                          <PrimaryButton
                            title={t('followButton.follow')}
                            onClick={() => vm.onFollowUserClick(user)}
                          />
                        ) : (
                          <SecondaryButton title={t('followButton.pending')} />
                        ))}
                    </FlexRow>
                  );
                })}
              </View>
            ) : (
              <View style={{ justifyContent: 'center', alignItems: 'center' }}>
                {vm.isLoading ? (
                  <ClipLoader size={30} color={colors.primary} loading={true} />
                ) : (
                  <P2>{vm.noResultsText}</P2>
                )}
              </View>
            )}
          </LowerContainer>
        </Container>
      </Modal>
    </ThemeProvider>
  );
});
